import React, { useCallback, useMemo } from 'react';
import { useExpanded, useTable } from 'react-table';

import { ChevronSmallDown as ChevronSmallDownIcon } from '@styled-icons/entypo/ChevronSmallDown';
import { ChevronSmallRight as ChevronSmallRightIcon } from '@styled-icons/entypo/ChevronSmallRight';

import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  HStack,
  Icon,
  Box,
} from '@chakra-ui/react';

import Loader from 'components/Loader';

import { CompositionGroup } from 'types/composition';

import CompositionGroupsTableRow from './CompositionGroupTableRow';
import Level2GroupsTable from './Level2GroupsTable';
import { Row } from './types';

type Props = {
  data: CompositionGroup[];
  loading: boolean;
  resourceType: 'service' | 'input';
  onSelectGroup: (parent: CompositionGroup, group: CompositionGroup) => void;
  onRemoveGroup?: (parent: CompositionGroup, group: CompositionGroup) => void;
  isGroupSelected?: (
    parent: CompositionGroup,
    group: CompositionGroup,
  ) => boolean;
  multiSelect?: boolean;
};

const CompositionGroupsTable: React.FC<Props> = ({
  data,
  loading,
  resourceType,
  onSelectGroup,
  onRemoveGroup,
  isGroupSelected,
  multiSelect = false,
}) => {
  const columns = useMemo(
    () => [
      {
        Header: 'Código',
        id: 'expander',
        width: '1%',
        Cell: ({ row }: { row: Row }) => {
          return (
            <HStack>
              {!!row.original.key &&
                (row.isExpanded && row.original.key ? (
                  <Icon as={ChevronSmallDownIcon} />
                ) : (
                  <Icon as={ChevronSmallRightIcon} />
                ))}

              <Box
                display="flex"
                flexDir="column"
                ml="1"
                justifyContent="center"
                alignItems="center"
                fontWeight="bold"
              >
                {row.original.key}
              </Box>
            </HStack>
          );
        },
      },
      {
        Header: 'Descrição',
        accessor: 'data.budget_composition_price.code',
        Cell: ({ row }: { row: Row }) => {
          return row.original.description || '-';
        },
      },
    ],
    [],
  );

  const uniqueData = useMemo(() => {
    const seen = new Set<string>();

    return data.filter((group) => {
      const identifier = `${group.key}-${group.description}`;
      if (seen.has(identifier)) return false;

      seen.add(identifier);
      return true;
    });
  }, [data]);

  const getRowId = useCallback((row) => {
    return `${row.key}-${row.description}`;
  }, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
  } = useTable(
    {
      // @ts-expect-error types
      columns,
      data: uniqueData,
      getRowId,
      autoResetExpanded: false,
    },
    useExpanded,
  );

  const renderRowSubComponent = useCallback(
    ({ group }) => (
      <Level2GroupsTable
        resourceType={resourceType}
        parentGroup={group}
        onSelectGroup={onSelectGroup}
        onRemoveGroup={onRemoveGroup}
        isGroupSelected={isGroupSelected}
        multiSelect={multiSelect}
      />
    ),
    [isGroupSelected, multiSelect, onRemoveGroup, onSelectGroup, resourceType],
  );

  return (
    <TableContainer width="100%">
      <Table {...getTableProps()} borderBottom="none" variant="simple">
        <Thead>
          {headerGroups.map((headerGroup) => (
            <Tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <Th
                  {...column.getHeaderProps()}
                  width={column.width}
                  // @ts-expect-error isNumeric does not exists on lib type
                  isNumeric={column.isNumeric}
                >
                  {column.render('Header')}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>

        <Tbody {...getTableBodyProps()}>
          {!loading &&
            rows.length > 0 &&
            rows.map((row) => {
              prepareRow(row);

              return (
                <CompositionGroupsTableRow
                  row={row as unknown as Row}
                  visibleColumns={visibleColumns}
                  renderRowSubComponent={renderRowSubComponent}
                  {...row.getRowProps()}
                />
              );
            })}

          {loading && (
            <Tr>
              <Td
                colSpan={visibleColumns.length}
                borderBottom="none"
                background="gray.100"
              >
                <Loader />
              </Td>
            </Tr>
          )}

          {!loading && uniqueData.length === 0 && (
            <Tr>
              <Td
                colSpan={visibleColumns.length}
                borderBottom="none"
                background="gray.100"
                className="text-center"
              >
                Nenhum resultado encontrado.
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default CompositionGroupsTable;
